/**
 * @module core/api/entity-api
 * @description Entity-specific API operations including metadata retrieval,
 * file listing, and search functionality across all entity types.
 */

import { get, handleResponse, buildQueryString } from './api-client.js';
import { ENTITY_TYPES } from '../../features/entity/entity-types.js';
import { logger } from '../../utils/logger.js';

/**
 * Get metadata for a specific entity
 *
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {string} entityId - ID of the entity
 * @returns {Promise<Object>} Entity metadata including name, SUDB ID, and other details
 * @throws {Error} If request fails or entity not found
 *
 * @example
 * const metadata = await getEntityMetadata('projects', '12345');
 * console.log(metadata.entityName); // "Project Name"
 */
export async function getEntityMetadata(entityType, entityId) {
    if (!entityType || !entityId) {
        throw new Error('Entity type and ID are required');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    const response = await get(`/api/${apiEntityType}/${entityId}/metadata`);
    return await handleResponse(response, `Failed to load ${entityType} metadata`);
}

/**
 * Get files list for a specific entity
 *
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts, dc-photos)
 * @param {string} entityId - ID of the entity
 * @returns {Promise<Array>} Array of file objects
 * @throws {Error} If request fails
 *
 * @example
 * const files = await getEntityFiles('projects', '12345');
 * files.forEach(file => console.log(file.fileName));
 */
export async function getEntityFiles(entityType, entityId) {
    if (!entityType || !entityId) {
        throw new Error('Entity type and ID are required');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    // Build query parameters
    const params = {};

    // Add category filter if entity type specifies one
    if (config?.categoryFilter) {
        params.category = config.categoryFilter;
        logger.debug(`📸 Requesting backend filter by category: ${config.categoryFilter}`);
    }

    // Build query string
    const queryString = buildQueryString(params);
    const fullUrl = `/api/${apiEntityType}/${entityId}/files${queryString}`;

    logger.debug(`🌐 API Request: ${fullUrl}`);
    const response = await get(fullUrl);
    const data = await handleResponse(response, `Failed to load ${entityType} files`);

    // Handle backend response format with category filtering
    // Backend returns: { files: [...], filtered: true, filterCategory: "dc-photos" }
    // Or legacy format: [...]
    let files;
    let backendFiltered = false;

    if (data && typeof data === 'object' && Array.isArray(data.files)) {
        files = data.files;
        backendFiltered = data.filtered === true;
        logger.debug(`📦 Backend returned ${files.length} files (backend filtered: ${backendFiltered})`);
    } else {
        // Fallback to legacy format (direct array)
        files = Array.isArray(data) ? data : [];
        logger.debug(`📦 Backend returned ${files.length} files (legacy format)`);
    }

    // Apply client-side filtering ONLY if backend didn't filter and categoryFilter is specified
    if (config?.categoryFilter && Array.isArray(files) && files.length > 0) {
        logger.debug(`🔍 Checking filtering for category: ${config.categoryFilter}`);

        // Normalize category filter for comparison (case-insensitive, handle spaces and hyphens)
        const normalizeCategory = (cat) => cat.toLowerCase().replace(/[\s-]+/g, '');
        const filterCategoryNormalized = normalizeCategory(config.categoryFilter);

        logger.debug(`🔍 Files before filtering:`, files.map(f => ({ name: f.fileName, category: f.fileCategory })));

        // Check if backend already filtered (all files match the category or backend confirmed filtering)
        const filesMatchingCategory = files.filter(file =>
            file.fileCategory && normalizeCategory(file.fileCategory) === filterCategoryNormalized
        );

        const allFilesMatch = filesMatchingCategory.length === files.length;

        logger.debug(`🔍 Files matching category '${config.categoryFilter}': ${filesMatchingCategory.length}/${files.length}`);

        if (backendFiltered || allFilesMatch) {
            logger.debug(`✅ Backend filtering worked: ${files.length} ${config.categoryFilter} files`);
        } else {
            logger.warn(`⚠️ Backend filtering failed - applying client-side filter for: ${config.categoryFilter}`);
            const originalCount = files.length;
            files = filesMatchingCategory;
            logger.debug(`✅ Client-side filtered ${originalCount} files down to ${files.length} ${config.categoryFilter} files`);
        }
    }

    return files;
}

/**
 * Search entities of a specific type
 *
 * @param {string} entityType - Type of entity to search (projects, opportunities, leads, contacts)
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Array of matching entities with metadata
 * @throws {Error} If request fails
 *
 * @example
 * const results = await searchEntities('projects', 'Smith House');
 * results.forEach(project => console.log(project.entityName));
 */
export async function searchEntities(entityType, searchTerm) {
    if (!entityType) {
        throw new Error('Entity type is required');
    }

    if (!searchTerm || searchTerm.trim().length < 3) {
        throw new Error('Search term must be at least 3 characters');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    const queryString = buildQueryString({ q: searchTerm.trim() });
    const response = await get(`/api/${apiEntityType}${queryString}`);

    const data = await handleResponse(response, `Failed to search ${entityType}`);

    // Validate response format
    if (!Array.isArray(data)) {
        logger.warn('Invalid API response format: expected array, got:', typeof data);
        return [];
    }

    return data;
}

/**
 * Perform universal search across all entity types
 * Searches projects, opportunities, leads, and contacts in parallel
 *
 * @param {string} searchTerm - Search term
 * @param {Array<string>} entityTypes - Array of entity types to search (defaults to all)
 * @returns {Promise<Object>} Search results grouped by entity type with total count
 * @throws {Error} If search term is too short
 *
 * @example
 * const results = await universalSearch('Smith');
 * console.log(results.totalCount); // Total results across all types
 * console.log(results.results.projects); // Array of matching projects
 */
export async function universalSearch(searchTerm, entityTypes = ['projects', 'opportunities', 'leads', 'contacts']) {
    if (!searchTerm || searchTerm.trim().length < 3) {
        throw new Error('Search term must be at least 3 characters');
    }

    try {
        logger.debug(`🔍 Performing universal search for: "${searchTerm}"`);

        // Create array of search promises for each entity type
        const searchPromises = entityTypes.map(async (entityType) => {
            try {
                logger.debug(`  → Searching ${entityType}...`);
                const results = await searchEntities(entityType, searchTerm);
                logger.debug(`  ✓ Found ${results.length} ${entityType}`);

                return {
                    entityType,
                    results: Array.isArray(results) ? results : []
                };
            } catch (error) {
                logger.error(`❌ Error searching ${entityType}:`, error);
                return { entityType, results: [] };
            }
        });

        // Wait for all searches to complete
        const searchResults = await Promise.all(searchPromises);

        // Group results by entity type
        const groupedResults = {};
        let totalCount = 0;

        searchResults.forEach(({ entityType, results }) => {
            groupedResults[entityType] = results;
            totalCount += results.length;
        });

        logger.debug(`✅ Universal search complete: ${totalCount} total results`);

        return {
            results: groupedResults,
            totalCount,
            searchTerm: searchTerm.trim()
        };

    } catch (error) {
        logger.error('❌ Error performing universal search:', error);
        throw error;
    }
}

/**
 * Load entity with both metadata and files
 * Convenience method that fetches both metadata and files in parallel
 *
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {string} entityId - ID of the entity
 * @returns {Promise<Object>} Object containing metadata and files
 * @throws {Error} If request fails
 *
 * @example
 * const { metadata, files } = await loadEntity('projects', '12345');
 * console.log(`${metadata.entityName} has ${files.length} files`);
 */
export async function loadEntity(entityType, entityId) {
    if (!entityType || !entityId) {
        throw new Error('Entity type and ID are required');
    }

    try {
        // Fetch metadata and files in parallel
        const [metadata, files] = await Promise.all([
            getEntityMetadata(entityType, entityId),
            getEntityFiles(entityType, entityId)
        ]);

        return { metadata, files };
    } catch (error) {
        logger.error(`Error loading entity ${entityType}/${entityId}:`, error);
        throw error;
    }
}

/**
 * Get all entities of a specific type (for browsing/listing)
 *
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {Object} options - Query options (limit, offset, etc.)
 * @returns {Promise<Array>} Array of entities
 * @throws {Error} If request fails
 *
 * @example
 * const projects = await getAllEntities('projects', { limit: 50 });
 */
export async function getAllEntities(entityType, options = {}) {
    if (!entityType) {
        throw new Error('Entity type is required');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    const queryString = buildQueryString(options);
    const response = await get(`/api/${apiEntityType}${queryString}`);

    const data = await handleResponse(response, `Failed to load ${entityType}`);

    // Validate response format
    if (!Array.isArray(data)) {
        logger.warn('Invalid API response format: expected array');
        return [];
    }

    return data;
}
